import { useState, useEffect, useCallback } from 'react';
import { useDroplet } from '../context/DropletContext';
import { useInterval } from './useInterval';

export interface ContainerStatus {
  name: string;
  running: boolean;
  health: string;
}

export interface ResourceStats {
  diskRootPercent: number;
  // null on droplets with no attached block volume.
  blockStoragePercent: number | null;
  memoryUsedPercent: number;
  loadAverage1m: number;
}

export interface NginxVhost {
  vhost: string;
  status: string;
}

export interface ApplicationHealth {
  application: string;
  status: string;
}

export interface ServiceStatus {
  name: string;
  active: boolean;
  state: string;
}

export interface DiskCleanupStatus {
  enabled: boolean;
  lastRunAt: string | null;
  lastRunTier: number | null;
  lastRunFreedPercent: number | null;
  // Optional: absent on a collector build that predates the every-cycle
  // orphan anonymous-volume prune.
  orphanVolumesPending?: number | null;
  orphanVolumesLastPrunedAt?: string | null;
  orphanVolumesLastPrunedCount?: number | null;
}

// Portfolio backups: read-only status (restores and backup management stay SSH-only).
export interface BackupOnServer {
  lastSetId: string | null;
  lastSetFinishedAt: string | null;
  status: 'ok' | 'degraded' | 'failed' | null;
  archivesOk: number | null;
  archivesFailed: number | null;
  setsRetained: number | null;
  oldestSetId: string | null;
  bytes: number | null;
}

export interface BackupOffsitePush {
  target: string;
  status: 'ok' | 'failed' | 'skipped' | null;
  pushedAt: string | null;
  setId: string | null;
}

export interface BackupVenture {
  venture: string;
  archives: number | null;
  bytes: number | null;
}

export interface BackupRestoreTest {
  source: string | null;
  setId: string | null;
  status: 'passed' | 'failed' | null;
  finishedAt: string | null;
  checksPassed: number | null;
  checksFailed: number | null;
}

export interface BackupOffsiteStore {
  setsStored: number | null;
  bytes: number | null;
  quarantined: number | null;
  newestSetId: string | null;
  oldestSetId: string | null;
  lastReceivedSetId: string | null;
  lastReceivedAt: string | null;
  checkedAt: string | null;
  lastResult: 'ok' | 'failed' | null;
}

export interface BackupStatus {
  onServer?: BackupOnServer | null;
  offsite?: BackupOffsitePush[];
  ventures?: BackupVenture[];
  restoreTest?: BackupRestoreTest | null;
  keyFingerprint?: string | null;
  offsiteStore?: BackupOffsiteStore | null;
}

export interface StatusFeed {
  schemaVersion: number;
  generatedAt: string;
  collectorStatus: string;
  containers: ContainerStatus[];
  resources: ResourceStats;
  nginx: NginxVhost[];
  applications: ApplicationHealth[];
  diskCleanup?: DiskCleanupStatus;
  // systemd units — reported by secondary droplets only.
  services?: ServiceStatus[];
  backups?: BackupStatus;
}

/**
 * Reads the sanitized monitoring feed (GET /api/v1/status). The backend has
 * already validated schema/bounds/staleness before this ever runs — a 503
 * or any fetch failure means "unavailable," which this hook surfaces
 * explicitly rather than showing stale or fabricated data.
 */
export function useStatus(pollIntervalMs = 15000) {
  const { api, selectedDroplet } = useDroplet();
  const feedId = selectedDroplet.feedId;
  const [data, setData] = useState<StatusFeed | null>(null);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.getStatus(feedId);
      if (response.available && response.data) {
        setData(response.data);
        setAvailable(true);
        setError(null);
      } else {
        setAvailable(false);
        setError(response.error || 'Monitoring data unavailable');
      }
    } catch (err) {
      setAvailable(false);
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  }, [api, feedId]);

  useEffect(() => {
    // Never show the previous droplet's data under the newly selected one.
    setData(null);
    setLoading(true);
    void fetchStatus();
  }, [fetchStatus]);

  useInterval(() => {
    void fetchStatus();
  }, pollIntervalMs);

  return { data, available, loading, error, refetch: fetchStatus };
}
